import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileDown, Send, CheckCircle2, Pencil, Mail, Wallet, Trash2, Plus, MessageCircle, PiggyBank } from "lucide-react";
import { useAuth, hasModule } from "@/lib/auth";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader } from "@/components/Common";
import { EmailDocumentDialog } from "@/components/EmailDocumentDialog";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { PaymentDialog } from "@/components/PaymentDialog";
import { PaymentClaimsPanel } from "@/components/PaymentClaimsPanel";
import { ProjectDetailsCard } from "@/components/ProjectDetailsCard";
import { AddInvoiceLineDialog } from "@/components/AddInvoiceLineDialog";
import { ActivityLog } from "@/components/ActivityLog";
import { openWaForInvoice } from "@/lib/whatsapp";
import { fmtMoney, fmtPrice, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function Pill({ value, t }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-amber-50 text-amber-800 ring-amber-200",
    paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset ${map[value] || map.draft}`}>{t("status_" + value)}</span>;
}

function PaymentStatusPill({ value }) {
  const map = {
    unpaid: { bg: "bg-rose-50 text-rose-700 ring-rose-200", l: "Unpaid" },
    partial: { bg: "bg-amber-50 text-amber-700 ring-amber-200", l: "Partial" },
    paid: { bg: "bg-emerald-50 text-emerald-700 ring-emerald-200", l: "Paid" },
  };
  const s = map[value] || map.unpaid;
  return (
    <span data-testid={`payment-status-${value}`}
          className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset ${s.bg}`}>
      {s.l}
    </span>
  );
}

const LINE_TYPE_BADGES = {
  item: null,
  labour_hourly: { l: "Labour · hr", bg: "bg-amber-50 text-amber-800 ring-amber-200" },
  labour_daily: { l: "Labour · day", bg: "bg-amber-50 text-amber-800 ring-amber-200" },
  charge: { l: "Charge", bg: "bg-slate-100 text-slate-700 ring-slate-200" },
};

export default function InvoiceDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["invoice", id], queryFn: () => api.getInvoice(id),
  });

  const [editOpen, setEditOpen] = useState(false);
  // Auto-open the edit dialog when navigated via `?edit=1` (Invoices list Pencil icon).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("edit") === "1") {
      setEditOpen(true);
      // Strip the query param so a page refresh doesn't re-open the dialog.
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [emailOpen, setEmailOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form, setForm] = useState({
    invoice_number: "",
    issue_date: "", due_date: "", tax_rate: 0, currency: "USD",
    payment_method: "", terms: "", notes: "",
    discount_type: "none", discount_value: 0,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["invoice-payments", id],
    queryFn: () => api.listInvoicePayments(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (data) {
      setForm({
        invoice_number: data.invoice_number || "",
        issue_date: (data.issue_date || "").slice(0, 10),
        due_date: (data.due_date || "").slice(0, 10),
        tax_rate: data.tax_rate || 0,
        currency: data.currency || "USD",
        payment_method: data.payment_method || "",
        terms: data.terms || "",
        notes: data.notes || "",
        discount_type: data.discount_type || "none",
        discount_value: data.discount_value || 0,
      });
    }
  }, [data]);

  const statusMut = useMutation({
    mutationFn: (s) => api.updateInvoiceStatus(id, s),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Status updated");
    },
  });
  const paymentMut = useMutation({
    mutationFn: (body) => api.recordInvoicePayment(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoice-payments", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
  const deletePaymentMut = useMutation({
    mutationFn: (pid) => api.deleteInvoicePayment(id, pid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoice-payments", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Payment removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete payment"),
  });
  const updateMut = useMutation({
    mutationFn: (body) => api.updateInvoice(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setEditOpen(false);
      toast.success("Invoice updated");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const addLineMut = useMutation({
    mutationFn: (body) => api.addInvoiceLine(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
  const deleteLineMut = useMutation({
    mutationFn: (lineIndex) => api.deleteInvoiceLine(id, lineIndex),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Line removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove line"),
  });

  const { data: reimbursables = [] } = useQuery({
    queryKey: ["invoice-expenses", id],
    queryFn: () => api.listExpenses({ invoice_id: id }),
    enabled: !!id && hasModule(user, "expenses"),
  });

  if (isLoading || !data) return <p className="text-muted-foreground">{t("loading")}</p>;

  return (
    <div data-testid="invoice-detail-page">
      <Link to="/invoices" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="size-3.5 mr-1" /> {t("invoices")}
      </Link>
      <PageHeader
        overline={t("invoice_number")}
        title={data.invoice_number}
        subtitle={
          <span className="inline-flex items-center gap-3 text-sm flex-wrap">
            <span>{t("issue_date")}: <span className="mono">{fmtDate(data.issue_date)}</span></span>
            <span>{t("due_date")}: <span className="mono">{fmtDate(data.due_date) || "—"}</span></span>
            <Pill value={data.status} t={t} />
            <PaymentStatusPill value={data.payment_status || "unpaid"} />
          </span>
        }
        actions={
          <>
            {hasModule(user, "project_funds") && (
              <Button data-testid="invoice-project-fund-btn" variant="outline" asChild>
                <Link to={`/project-funds?from_invoice=${id}`}><PiggyBank className="size-4 mr-1.5" /> Project fund</Link>
              </Button>
            )}
            <Button data-testid="edit-invoice-btn" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4 mr-1.5" /> {t("edit")}
            </Button>
            <Button data-testid="download-invoice-pdf-btn" variant="outline" onClick={() => setPreviewOpen(true)}>
              <FileDown className="size-4 mr-1.5" /> {t("download_pdf")}
            </Button>
            <Button data-testid="email-invoice-btn" variant="outline" onClick={() => setEmailOpen(true)}>
              <Mail className="size-4 mr-1.5" /> Email
            </Button>
            <Button
              data-testid="wa-invoice-btn"
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              onClick={() => openWaForInvoice(data, lang)}
              title="Follow up via WhatsApp"
            >
              <MessageCircle className="size-4 mr-1.5" /> WhatsApp
            </Button>
            {data.status === "draft" && (
              <Button data-testid="mark-sent-btn" variant="outline" onClick={() => statusMut.mutate("sent")}>
                <Send className="size-4 mr-1.5" /> {t("mark_sent")}
              </Button>
            )}
            {data.payment_status !== "paid" && data.status !== "cancelled" && (
              <Button data-testid="record-payment-btn" onClick={() => setPaymentOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Wallet className="size-4 mr-1.5" /> Record payment
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("client")}</p>
          <p className="font-display font-semibold text-lg">{data.client_snapshot?.name}</p>
          <p className="text-xs mono text-muted-foreground mt-2">{data.client_snapshot?.email}</p>
          <p className="text-xs mono text-muted-foreground">{data.client_snapshot?.phone}</p>
          <p className="text-xs text-muted-foreground mt-2">{data.client_snapshot?.address}</p>
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("source")}</p>
          <p className="font-medium text-sm capitalize">{(data.source || "manual").replace("_", " ")}</p>
          <p className="text-xs mono text-muted-foreground">{data.source_number || "—"}</p>
          <p className="overline mb-1 mt-3">{t("currency")}</p>
          <p className="mono text-sm">{data.currency}</p>
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("payment_method")}</p>
          <p className="text-sm">{data.payment_method || "—"}</p>
          {data.payment_date && (
            <>
              <p className="overline mb-1 mt-3">{t("payment_date")}</p>
              <p className="text-xs mono text-emerald-700">{fmtDate(data.payment_date)}</p>
            </>
          )}
        </div>
      </div>

      <ProjectDetailsCard
        docId={data.id}
        initial={data.project_details}
        patchFn={api.updateInvoice}
        invalidateKeys={[["invoice", id], ["invoices"]]}
        testIdPrefix="invoice-project"
      />

      <div className="border border-border rounded-md bg-white overflow-x-auto mb-5">
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <p className="overline">Line items</p>
          {data.status !== "cancelled" && (
            <Button
              data-testid="add-service-line-btn"
              variant="outline"
              size="sm"
              className="text-primary border-primary/40 hover:bg-primary/5"
              onClick={() => setAddLineOpen(true)}
            >
              <Plus className="size-3.5 mr-1" /> Add service / labour
            </Button>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="overline">#</TableHead>
              <TableHead className="overline">{t("item_name")}</TableHead>
              <TableHead className="overline">{t("specification")}</TableHead>
              <TableHead className="overline text-right">{t("qty")}</TableHead>
              <TableHead className="overline text-right">{t("price")}</TableHead>
              <TableHead className="overline text-right">Disc %</TableHead>
              <TableHead className="overline text-right">{t("line_total")}</TableHead>
              <TableHead className="overline text-right w-10">&nbsp;</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.items || []).map((l, i) => {
              const badge = LINE_TYPE_BADGES[l.line_type || "item"];
              const isManual = ["labour_hourly", "labour_daily", "charge"].includes(l.line_type);
              return (
                <TableRow key={i} data-testid={`invoice-line-${i}`}>
                  <TableCell className="mono text-xs text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{l.name}</span>
                      {badge && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ring-1 ring-inset ${badge.bg}`}>
                          {badge.l}
                        </span>
                      )}
                    </div>
                    {(l.brand || l.model) && (
                      <div className="text-[11px] text-muted-foreground">{[l.brand, l.model].filter(Boolean).join(" · ")}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {l.specification || "—"}
                    {(l.serials || []).filter(Boolean).length > 0 && (
                      <div className="text-[10px] text-primary mt-0.5 mono">
                        S/N: {(l.serials || []).filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right mono">{l.qty} {l.unit ? <span className="text-[10px] text-muted-foreground">{l.unit}</span> : null}</TableCell>
                  <TableCell className="text-right mono">{fmtPrice(l.unit_price, data.currency)}</TableCell>
                  <TableCell className="text-right mono text-xs text-muted-foreground">
                    {(() => {
                      const pct = Number(l.line_discount_pct || 0);
                      const amt = Number(l.line_discount || 0);
                      if (pct <= 0 && amt <= 0) return "—";
                      if (pct > 0 && amt > 0) return <><span>{pct.toFixed(0)}%</span><br /><span className="text-[10px]">({fmtMoney(amt)})</span></>;
                      return fmtMoney(amt);
                    })()}
                  </TableCell>
                  <TableCell className="text-right mono font-medium">{fmtPrice(l.line_total, data.currency)}</TableCell>
                  <TableCell className="text-right">
                    {isManual && data.status !== "cancelled" ? (
                      <Button
                        data-testid={`delete-line-${i}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                        onClick={() => {
                          if (window.confirm(`Remove "${l.name}" from this invoice?`)) {
                            deleteLineMut.mutate(i);
                          }
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Client-reported payments awaiting review */}
      <PaymentClaimsPanel invoiceId={id} />

      {/* Payments history */}
      {payments.length > 0 && (
        <div className="border border-border rounded-md bg-emerald-50/40 overflow-x-auto mb-5" data-testid="payments-section">
          <div className="px-4 pt-3 pb-1 flex justify-between items-baseline">
            <p className="overline text-emerald-700">Payments received</p>
            <p className="text-[11px] text-muted-foreground">{payments.length} payment{payments.length === 1 ? "" : "s"} recorded</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Date</TableHead>
                <TableHead className="overline">Method</TableHead>
                <TableHead className="overline">Reference</TableHead>
                <TableHead className="overline">Notes</TableHead>
                <TableHead className="overline text-right">Amount</TableHead>
                <TableHead className="overline text-right">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell className="text-xs mono text-muted-foreground">{fmtDate(p.paid_at)}</TableCell>
                  <TableCell className="capitalize">{(p.method || "").replace("_", " ")}</TableCell>
                  <TableCell className="text-xs mono">{p.reference || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.notes || "—"}</TableCell>
                  <TableCell className="text-right mono font-semibold text-emerald-700">
                    {fmtMoney(p.amount)} {p.currency}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`delete-payment-${p.id}`} variant="ghost" size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              if (window.confirm("Delete this payment? Invoice status will recompute.")) {
                                deletePaymentMut.mutate(p.id);
                              }
                            }}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex justify-end">
        <div className="w-full max-w-sm border border-border rounded-md bg-white p-5">
          <div className="space-y-2 mono text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("subtotal")}</span><span data-testid="invoice-subtotal">{fmtPrice(data.subtotal, data.currency)}</span></div>
            {Number(data.discount_amount || 0) > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>Discount {data.discount_type === "percent" ? `(${Number(data.discount_value).toFixed(0)}%)` : ""}</span>
                <span data-testid="invoice-discount">-{fmtPrice(data.discount_amount, data.currency)}</span>
              </div>
            )}
            {!(Number(data.discount_amount || 0) > 0) && data.status !== "paid" && (
              <button
                type="button"
                data-testid="add-discount-hint"
                onClick={() => setEditOpen(true)}
                className="text-[10px] text-primary hover:underline text-left w-full"
              >
                + Add a discount (edit invoice)
              </button>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">{t("tax")} ({data.tax_rate || 0}%)</span><span data-testid="invoice-tax">{fmtPrice(data.tax, data.currency)}</span></div>
            <div className="border-t border-border pt-2 flex justify-between text-base font-bold text-primary">
              <span>{t("total_due")}</span>
              <span data-testid="invoice-total">{fmtPrice(data.total, data.currency)}</span>
            </div>
            {Number(data.paid_amount || 0) > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Paid to date</span>
                <span data-testid="invoice-paid">-{fmtPrice(data.paid_amount, data.currency)}</span>
              </div>
            )}
            <div className={`border-t border-border pt-2 flex justify-between text-base font-bold ${Number(data.balance_due ?? data.total) <= 0.005 ? "text-emerald-700" : "text-rose-700"}`}>
              <span>Balance due</span>
              <span data-testid="invoice-balance">{fmtPrice(data.balance_due ?? data.total, data.currency)}</span>
            </div>
            {data.payment_status === "paid" && (
              <div className="mt-2 px-3 py-2 rounded-md bg-emerald-100 text-emerald-800 text-center text-sm font-bold ring-1 ring-emerald-300">
                <CheckCircle2 className="size-4 inline-block mr-1.5" /> PAID IN FULL
              </div>
            )}
          </div>
        </div>
      </div>

      {reimbursables.length > 0 && (
        <div className="mt-5 border border-emerald-200 bg-emerald-50/30 rounded-md p-4" data-testid="reimbursable-expenses">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Wallet className="size-4 text-emerald-700" />
              <span className="overline">Reimbursable expenses ({reimbursables.length})</span>
            </div>
            <a
              href="/expenses"
              className="text-[11px] text-primary hover:underline"
              data-testid="reimbursable-manage-link"
            >
              Manage in Expenses →
            </a>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Pass-through costs incurred for this client. Add them to the invoice using the "Add service / labour" button as a Fixed charge line.
          </p>
          <div className="divide-y divide-emerald-100">
            {reimbursables.map((e) => (
              <div key={e.id} data-testid={`reimbursable-${e.id}`} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{e.description}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {fmtDate(e.expense_date)} · {e.category}
                    {e.vendor ? ` · ${e.vendor}` : ""}
                  </div>
                </div>
                <div className="mono font-semibold text-emerald-800">
                  {fmtMoney(e.amount)} {e.currency}
                </div>
              </div>
            ))}
            <div className="pt-2 flex justify-between font-semibold text-sm">
              <span>Reimbursable total</span>
              <span className="mono text-emerald-800">
                {fmtMoney(reimbursables.reduce((s, e) => s + Number(e.amount || 0), 0))} {data.currency}
              </span>
            </div>
          </div>
        </div>
      )}


      {(data.terms || data.notes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          {data.terms && (
            <div className="border border-border rounded-md bg-white p-5">
              <p className="overline mb-2">{t("terms")}</p>
              <p className="text-sm whitespace-pre-wrap">{data.terms}</p>
            </div>
          )}
          {data.notes && (
            <div className="border border-border rounded-md bg-white p-5">
              <p className="overline mb-2">{t("notes")}</p>
              <p className="text-sm whitespace-pre-wrap">{data.notes}</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-invoice-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("edit")} — {data.invoice_number}</DialogTitle>
            <DialogDescription className="text-xs">{data.client_snapshot?.name}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className="overline">Invoice #</Label>
              <Input data-testid="inv-number" className="mono" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
            <div><Label className="overline">{t("issue_date")}</Label>
              <Input data-testid="inv-issue-date" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
            <div><Label className="overline">{t("due_date")}</Label>
              <Input data-testid="inv-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label className="overline">{t("tax_rate")} %</Label>
              <Input data-testid="inv-tax-rate" type="number" min="0" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
            <div><Label className="overline">{t("currency")}</Label>
              <Input data-testid="inv-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
            <div><Label className="overline">Discount type</Label>
              <select
                data-testid="inv-discount-type"
                value={form.discount_type}
                onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm">
                <option value="none">No discount</option>
                <option value="percent">Percent (%)</option>
                <option value="amount">Fixed amount</option>
              </select></div>
            <div><Label className="overline">Discount value</Label>
              <Input data-testid="inv-discount-value" type="number" min="0" step="0.01"
                     disabled={form.discount_type === "none"}
                     value={form.discount_value}
                     onChange={(e) => setForm({ ...form, discount_value: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="overline">{t("payment_method")}</Label>
              <Input data-testid="inv-payment-method" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="Bank transfer / Cash / …" /></div>
            <div className="sm:col-span-2"><Label className="overline">{t("terms")}</Label>
              <Textarea data-testid="inv-terms" rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Net 30 …" /></div>
            <div className="sm:col-span-2"><Label className="overline">{t("notes")}</Label>
              <Textarea data-testid="inv-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="inv-cancel">{t("cancel")}</Button>
            <Button data-testid="inv-save" className="bg-primary hover:bg-primary/90"
              disabled={updateMut.isPending}
              onClick={() => updateMut.mutate({
                ...form,
                tax_rate: Number(form.tax_rate || 0),
                discount_value: Number(form.discount_value || 0),
              })}>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={`Invoice ${data?.invoice_number || ""}`}
        filename={`invoice-${data?.invoice_number || data?.id}.pdf`}
        fetchBlob={() => api.fetchInvoicePdfBlob(data.id)}
      />

      <EmailDocumentDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        kind="invoice"
        defaultTo={data.client_snapshot?.email || ""}
        defaultClientName={data.client_snapshot?.name || ""}
        sendFn={(payload) => api.emailInvoice(data.id, payload)}
        testIdPrefix="email-invoice"
      />

      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        invoice={data}
        onSaved={(body) => paymentMut.mutateAsync(body)}
      />

      <AddInvoiceLineDialog
        open={addLineOpen}
        onOpenChange={setAddLineOpen}
        currency={data.currency}
        onSubmit={(body) => addLineMut.mutateAsync(body)}
      />

      <div className="mt-8">
        <ActivityLog resourceType="invoice" resourceId={data.id} />
      </div>
    </div>
  );
}
